import { useEffect, useCallback, useState, useMemo } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';

import { Button, Stack, Collapse, Modal, Box, colors } from '@mui/material';

import { useAlert } from '@/contexts/Alert/AlertContext';

import { UnitMixTabContent } from '@/components/BuildingTabs/UnitMixTabContent';
import { Breadcrumbs } from '@/components/shared/Breadcrumbs/Breadcrumbs';
import { TabListItem, Tabs } from '@/components/shared/Tabs/Tabs';

import { useAuth } from '@/shared/context/AuthProvider';
import {
  Project,
  Unit,
  ActionItem,
  UserData,
  LeaseLifecycle,
  AnalyzingFilter,
  Process,
} from '@urbanmix-tech/shared-js';
import { useLoading } from '@/shared/context/UIContextProvider';
import {
  getProjectById,
  getUnitsByProjectId,
  subscribeActionsByProjectId,
  getProcesses,
} from '@/data';

import { AnalyzeFilter } from '@/components/BuildingTabs/AnalyzeFilter';

import AppIcon from '@/components/shared/AppIcon/AppIcon';

import { ErrorBoundary } from '@sentry/react';
import { MyDashboardTabContent } from '@/components/BuildingTabs/MyDashboardTabContent';
import { ActionItemModal } from '@/components/ActionModals/units/ActionItemModal/ActionItemModal';
import GenericBuildingModel from '@/components/BuildingModel/BuildingModelGeneric';
import { TrackerTabContent } from '@/components/BuildingTabs/TrackerTabContent';

export default function Building() {
  const navigate = useNavigate();
  const { projectId, buildingId } = useParams<{
    projectId: string;
    buildingId: string;
  }>();
  const { createLoading } = useLoading();
  const { showAlert } = useAlert();
  const { userData, selectedOption, isEditMode, usersData } = useAuth();

  const [tabKey, setTabKey] = useState<string>('mydashboard');
  const [project, setProject] = useState<Project | null>(null);
  const [units, setUnits] = useState<Unit[]>([]);
  const [actionItems, setActionItems] = useState<ActionItem[]>([]);
  const [processes, setProcesses] = useState<Process[]>([]);
  const [error, setError] = useState<Error | null>(null);

  // Derive the user list/email-map from AuthProvider's already-loaded users
  // instead of re-reading the entire users collection on every Building open.
  const users = useMemo<UserData[]>(
    () => Object.values(usersData),
    [usersData]
  );
  const usersByEmail = useMemo<Record<string, UserData>>(() => {
    const map: Record<string, UserData> = {};
    Object.values(usersData).forEach((user) => {
      if (user?.email) {
        map[user.email] = user;
      }
    });
    return map;
  }, [usersData]);

  const [collapsed] = useState(false);
  const [hoveredUnitId, setHoveredUnitId] = useState<string | null>(null);

  const [selectedAction, setSelectedAction] = useState<ActionItem | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [buildingData, setBuildingData] = useState<Record<string, any>>({});

  const [filters, setFilters] = useState<{
    unitFilter: string[];
    statusFilter: LeaseLifecycle[];
    analyzeFilter: string[];
  }>({
    unitFilter: [],
    statusFilter: [],
    analyzeFilter: [AnalyzingFilter.None],
  });

  useEffect(() => {
    console.log('Log vars', users, error, usersByEmail);
  }, []);

  useEffect(() => {
    if (projectId) {
      const loader = createLoading();

      loader.startLoading({ title: 'Loading Project from db...' });

      getProjectById(projectId).then((value) => {
        if (value && value.buildings.length > 0) {
          setProject(value);
        } else {
          showAlert('Building not exists!', 'error');
        }

        loader.stopLoading();
      });

      getUnitsByProjectId(projectId).then((_units) => {
        setUnits(_units);
      });

      const subscribeToUpdates = async () => {
        const unsubscribe = await subscribeActionsByProjectId(
          projectId,
          (items: ActionItem[]) => setActionItems(items), // onUpdate callback
          (error) => setError(error) // onError callback
        );

        // Clean up the listener when the component is unmounted
        return () => unsubscribe();
      };

      const subscribeToProcesses = async () => {
        const unsubscribe = (await getProcesses(
          { projectId },
          (processes: Process[]) => {
            setProcesses(processes);
          }
        )) as () => void;
        return unsubscribe;
      };

      const unsubscribePromise = subscribeToUpdates();
      const unsubscribeProcessesPromise = subscribeToProcesses();

      return () => {
        unsubscribePromise.then((unsubscribe) => unsubscribe && unsubscribe());
        unsubscribeProcessesPromise.then((unsubscribe) => unsubscribe && unsubscribe());
      };
    }
    showAlert(
      'There is something wrong, please check your connectivity!',
      'error'
    );
    return () => {};
  }, [createLoading, projectId, showAlert]);

  const handleClickUnit = useCallback(
    (unitId: string) => {
      navigate(`units/${unitId}`);
    },
    [navigate]
  );

  const handleHoverUnit = useCallback((unitId: string | null) => {
    setHoveredUnitId(unitId);
    setHoveredUnitId(unitId);
  }, []);

  const handleChangeAnalyzeFilter = useCallback((selected: string[]) => {
    setFilters((oldFilters) => ({
      ...oldFilters,
      analyzeFilter: selected,
    }));
  }, []);

  const handleChangeModal = (open: boolean) => {
    if (open === false) {
      setSelectedAction(null);
    }
    setIsModalOpen(open);
  };

  const filteredUnits = useMemo(
    () =>
      units
        .filter((unit) => {
          if (filters.unitFilter.length > 0) {
            return filters.unitFilter.includes(`${unit.numBedrooms}br`);
          }

          return true;
        })
        .filter((unit) => {
          if (filters.statusFilter.length > 0) {
            return Array.isArray(unit.currentState)
              ? unit.currentState.length > 0
                ? filters.statusFilter.some((item) =>
                    unit.currentState.includes(item)
                  )
                : filters.statusFilter.includes(LeaseLifecycle.NOTICE)
              : filters.statusFilter.includes(
                  (unit.currentState as LeaseLifecycle) || LeaseLifecycle.NOTICE
                );
          }

          return true;
        }),
    [filters, units]
  );

  const handleClickActionItem = (actionItem: ActionItem) => {
    setSelectedAction(actionItem);
    setIsModalOpen(true);
  };

  const filteredActionItems = useMemo(() => {
    const unitIds = filteredUnits.map((unit) => unit.id);

    return actionItems.filter((action) => unitIds.includes(action.unitId));
  }, [actionItems, filteredUnits]);

  const userActionItems = useMemo(() => {
    return filteredActionItems.filter((action) => {
      if (action.status === 'done' && action.completedOn == null) {
        return false;
      }

      if (action.owner?.userId === userData?.id) {
        return true;
      }
      if (action.supervisor?.userId === userData?.id) {
        return true;
      }
      if (
        action.followers?.some((follower) => follower.userId === userData?.id)
      ) {
        return true;
      }

      if (!action.owner) {
        return true;
      }
      return false;
    });
  }, [filteredActionItems, userData]);

  if (!project) {
    return null;
  }

  const tabList: TabListItem[] = [
    {
      tabId: 'mydashboard',
      label: 'My Dashboard',
      iconName: 'eye',
      status: userActionItems.length,
    },
    {
      tabId: 'todos',
      label: 'All Tasks',
      iconName: 'actions',
      status: filteredActionItems.length,
    },
    {
      tabId: 'unitmix',
      label: 'UnitMix',
      iconName: 'file',
      status: filteredUnits.length,
    },
  ];

  const breadcrumbsPath = [
    {
      label:
        selectedOption.value !== 'all' ? selectedOption.label : 'Portfolio',
      href: '/portfolio',
    },
    {
      label: (
        <Stack direction="row" gap="4px" alignItems="center">
          {project?.name || ''}
          {project?.address && !project.address.includes(project?.name)
            ? `, ${project.address}`
            : ''}
          <AppIcon name="info" />
        </Stack>
      ),
    },
  ];

  return (
    <Stack gap="16px" className="flex h-full w-full min-w-[470px] p-24">
      <Breadcrumbs paths={breadcrumbsPath} />
      <Stack direction="row" alignItems="center" justifyContent="flex-end">
        {userData?.isDev && isEditMode && (
          <>
            <Link to={`/projects/${projectId}/${buildingId}/model`}>
              <Button variant="outlined">Edit Project</Button>
            </Link>
            <Link to={`/projects/${projectId}/${buildingId}/rentroll`}>
              <Button variant="outlined">Update Rent Roll</Button>
            </Link>
          </>
        )}
      </Stack>
      <Tabs items={tabList} tabKey={tabKey} setTabKey={setTabKey} />
      <Stack direction="row" className="h-[calc(100vh-18rem)] pb-20" gap="16px">
        <Collapse
          in={!collapsed}
          orientation="horizontal"
          className={twMerge(
            collapsed ? 'w-12/12' : 'w-6/12',
            '',
            'overflow-y-scroll'
          )}
        >
          <Stack
            gap="18px"
            className="wrapperInner-w-full  relative  flex-1 overflow-y-scroll"
          >
            {tabKey === 'mydashboard' ? (
              <MyDashboardTabContent
                actionItems={userActionItems}
                leaseStatusFilter={filters.statusFilter}
                onClickActionItem={(actionitem) => {
                  handleClickActionItem(actionitem);
                }}
                onBuildingDataChange={(data) => {
                  setBuildingData(data);
                }}
              />
            ) : null}
            {tabKey === 'todos' ? (
              <TrackerTabContent
                actionItems={filteredActionItems}
                processes={processes}
                leaseStatusFilter={filters.statusFilter}
                onClickActionItem={handleClickActionItem}
                onBuildingDataChange={(data) => {
                  setBuildingData(data);
                }}
              />
            ) : null}
            {tabKey === 'unitmix' ? (
              <UnitMixTabContent
                units={filteredUnits}
                onSelectUnit={handleHoverUnit}
                hoveredUnitId={hoveredUnitId}
                onHoverUnit={() => {}}
              />
            ) : null}
          </Stack>
        </Collapse>

        <Stack
          direction="row"
          alignItems="center"
          className={twMerge(
            'min-h-[700px] rounded-[11px] border border-neutral-400',
            collapsed ? 'w-full' : 'w-6/12'
          )}
        >
          {tabKey === 'unitmix' ? (
            <AnalyzeFilter
              className="absolute right-24 top-24 z-10"
              analyzeFilter={filters.analyzeFilter}
              onChangeAnalyzeFilter={handleChangeAnalyzeFilter}
            />
          ) : null}
          <ErrorBoundary
            fallback={
              <div>
                <p>Error loading model</p>
              </div>
            }
          >
            <GenericBuildingModel
              projectId={project.id}
              building={project.buildings[0]}
              units={filteredUnits}
              selectedUnitIds={hoveredUnitId ? [hoveredUnitId] : []}
              onSelectUnit={handleClickUnit}
              onHoverUnit={handleHoverUnit}
            />
          </ErrorBoundary>
        </Stack>
      </Stack>
      {selectedAction ? (
        <Modal
          open={isModalOpen}
          onClose={() => {
            setSelectedAction(null);
            setIsModalOpen(false);
          }}
        >
          <Box
            component="div"
            className="absolute left-1/2 top-[10vh] -translate-x-1/2 rounded-[10px] border border-stone-200 border-opacity-[0.15] shadow outline-none"
          >
            <ActionItemModal
              actionItem={selectedAction}
              onChangeModalOpen={handleChangeModal}
            />
          </Box>
        </Modal>
      ) : null}
    </Stack>
  );
}
